import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShieldIcon from '@mui/icons-material/Shield';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SpeedIcon from '@mui/icons-material/Speed';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const features = [
  {
    icon: <LocationOnIcon />,
    color: '#1075BA',
    bg: 'linear-gradient(135deg, rgba(16,117,186,0.12) 0%, rgba(58,157,228,0.08) 100%)',
    title: 'Real-Time Tracking',
    desc: 'Monitor every vehicle\'s live location on an interactive map. Get second-by-second position updates with route history playback.',
    tags: ['GPS Accuracy', 'Route History', 'Live Updates'],
  },
  {
    icon: <ShieldIcon />,
    color: '#F8B220',
    bg: 'linear-gradient(135deg, rgba(248,178,32,0.12) 0%, rgba(255,201,77,0.08) 100%)',
    title: 'Geofencing',
    desc: 'Create custom zones and get instant alerts when vehicles enter or exit designated areas. Protect restricted zones effortlessly.',
    tags: ['Custom Zones', 'Instant Alerts', 'Zone History'],
  },
  {
    icon: <ReportProblemIcon />,
    color: '#ef4444',
    bg: 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(239,68,68,0.05) 100%)',
    title: 'Issue Reporting',
    desc: 'Drivers can flag mechanical issues, accidents, or incidents directly from the app. Fleet managers get immediate notifications.',
    tags: ['Photo Uploads', 'Priority Levels', 'Auto-Routing'],
  },
  {
    icon: <SpeedIcon />,
    color: '#8b5cf6',
    bg: 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(139,92,246,0.05) 100%)',
    title: 'Driver Behavior',
    desc: 'Score drivers on harsh braking, rapid acceleration, and speeding. Reduce accidents and fuel costs with data-driven insights.',
    tags: ['Safety Score', 'Eco Driving', 'Leaderboard'],
  },
  {
    icon: <DirectionsCarIcon />,
    color: '#10b981',
    bg: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(16,185,129,0.05) 100%)',
    title: 'Fleet Management',
    desc: 'Centralized dashboard to manage all your vehicles, assign trips, schedule maintenance, and track fuel consumption.',
    tags: ['Trip Assignment', 'Fuel Tracking', 'Maintenance'],
  },
  {
    icon: <NotificationsActiveIcon />,
    color: '#1075BA',
    bg: 'linear-gradient(135deg, rgba(16,117,186,0.12) 0%, rgba(58,157,228,0.08) 100%)',
    title: 'Smart Alerts',
    desc: 'Get push notifications, SMS, or email alerts for speeding, zone breaches, idle time, and engine issues — all customizable.',
    tags: ['Multi-channel', 'Custom Rules', 'Escalations'],
  },
];

function FeatureCard({ feature, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        p: 3.5,
        borderRadius: '20px',
        background: feature.bg,
        border: '1px solid rgba(16,117,186,0.08)',
        height: '100%',
        cursor: 'default',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.6s ${index * 0.1}s ease, transform 0.6s ${index * 0.1}s ease`,
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: `0 20px 50px ${feature.color}22`,
          border: `1px solid ${feature.color}33`,
        },
      }}
    >
      <Box sx={{
        width: 52, height: 52, borderRadius: '14px',
        background: `${feature.color}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 2.5,
        '& .MuiSvgIcon-root': { color: feature.color, fontSize: 26 },
      }}>
        {feature.icon}
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0d1f35', mb: 1.5, fontSize: '1.15rem' }}>
        {feature.title}
      </Typography>
      <Typography sx={{ color: '#4a6580', mb: 2.5, fontSize: '0.9rem', lineHeight: 1.7 }}>
        {feature.desc}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {feature.tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            sx={{ bgcolor: `${feature.color}15`, color: feature.color, fontWeight: 600, fontSize: '0.7rem', border: `1px solid ${feature.color}30` }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function Features() {
  const [titleVisible, setTitleVisible] = useState(false);
  const titleRef = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setTitleVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
    if (titleRef.current) obs.observe(titleRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box id="features" sx={{ py: { xs: 10, md: 14 }, bgcolor: '#fff', position: 'relative', overflow: 'hidden' }}>
      {/* decorative */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
      }} />

      <Container maxWidth="lg">
        <Box ref={titleRef} sx={{ textAlign: 'center', mb: 8, opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'none' : 'translateY(20px)', transition: 'all 0.7s ease' }}>
          <Chip label="Features" sx={{ mb: 2.5, bgcolor: 'rgba(16,117,186,0.08)', color: '#1075BA', fontWeight: 700, border: '1px solid rgba(16,117,186,0.2)' }} />
          <Typography variant="h2" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, color: '#0d1f35', mb: 2 }}>
            Everything You Need to{' '}
            <Box component="span" sx={{ background: 'linear-gradient(135deg, #1075BA, #3a9de4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Run Your Fleet
            </Box>
          </Typography>
          <Typography sx={{ color: '#4a6580', fontSize: '1.05rem', maxWidth: 520, mx: 'auto' }}>
            A complete suite of tools designed for modern fleet operators — from solo dispatchers to enterprise logistics teams.
          </Typography>
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 3,
        }}>
          {features.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} />
          ))}
        </Box>
      </Container>
    </Box>
  );
}